import React, { useMemo } from "react";
import Highcharts from "highcharts/highmaps";
import HighchartsReact from "highcharts-react-official";
import { Box } from "@mui/material";
import { nFormatter } from "../../../Helpers";
import CurrencySymbol from "../../../Components/common/CurrencySymbol";
import WidgetShell from "./WidgetShell";
import WidgetSkeleton from "./WidgetSkeleton";
import { applyEnterpriseChartTheme, HC_CONTAINER_PROPS } from "./chartTheme";
import { ExdBackButton } from "./ExdControls";
import { EXD, ExdHint, chartWrapSx } from "./exdMui";

const AGING_COLORS = [
  "#8b4fd8",
  "#16a36a",
  "#14a3b8",
  "#d98a06",
  "#f08c1a",
  "#e0457b",
  "#d6334a",
  "#d6334a",
  "#d6334a",
];

const ReceivableAgingWidget = ({
  loading,
  translate,
  showAccountsData,
  showAccountsTitle,
  showAccountsLoader,
  overDue,
  overDueChart,
  accountsDataChart,
  onBack,
  loginUserCurrency,
  infoTitle,
}) => {
  const themedOverDue = useMemo(() => {
    if (!overDueChart || !Object.keys(overDueChart || {}).length) return null;
    const themed = applyEnterpriseChartTheme(overDueChart);
    const seriesData = themed?.series?.[0]?.data;
    if (Array.isArray(seriesData)) {
      themed.series = [
        {
          ...(themed.series[0] || {}),
          data: seriesData.map((point, index) => {
            const color = AGING_COLORS[index % AGING_COLORS.length];
            if (point && typeof point === "object" && !Array.isArray(point)) {
              return { ...point, color };
            }
            return { y: Number(point) || 0, color };
          }),
        },
      ];
    }
    themed.chart = {
      ...(themed.chart || {}),
      height: 290,
      backgroundColor: "transparent",
      style: { fontFamily: EXD.font },
    };
    themed.legend = { enabled: false };
    themed.title = { text: null };
    themed.plotOptions = {
      ...(themed.plotOptions || {}),
      column: {
        ...(themed.plotOptions?.column || {}),
        borderRadius: 5,
        borderWidth: 0,
        maxPointWidth: 46,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          style: {
            color: EXD.text,
            fontSize: "11px",
            fontWeight: "700",
            fontFamily: EXD.font,
            textOutline: "none",
          },
          formatter() {
            return nFormatter(this.y, 2);
          },
        },
      },
    };
    return themed;
  }, [overDueChart]);

  const themedAccounts = useMemo(() => {
    if (!accountsDataChart || !Object.keys(accountsDataChart || {}).length) {
      return null;
    }
    const themed = applyEnterpriseChartTheme(accountsDataChart);
    themed.chart = {
      ...(themed.chart || {}),
      height: 290,
      backgroundColor: "transparent",
      style: { fontFamily: EXD.font },
    };
    themed.legend = { enabled: false };
    themed.title = { text: null };
    return themed;
  }, [accountsDataChart]);

  const pastDue = Number(overDue?.pastDueTotal) || 0;

  return (
    <WidgetShell
      title={translate("Receivable Aging Summary")}
      subtitle={
        showAccountsData ? (
          <>
            · <Box component="b">{translate(showAccountsTitle)}</Box>
          </>
        ) : (
          <>
            · {translate("Grand Total")}{" "}
            <Box component="b">
              {overDue?.grandTotal ? nFormatter(overDue.grandTotal, 2) : 0}{" "}
              <CurrencySymbol
                currency={loginUserCurrency}
                withParentheses={false}
              />
            </Box>
            {pastDue ? (
              <>
                {" "}
                · past due{" "}
                <Box component="b" sx={{ color: EXD.bad }}>
                  {nFormatter(pastDue, 2)}
                </Box>
              </>
            ) : null}
          </>
        )
      }
      infoTitle={infoTitle}
      loading={loading}
      controls={
        showAccountsData ? (
          <ExdBackButton onClick={onBack}>{translate("Back")}</ExdBackButton>
        ) : null
      }
    >
      {showAccountsData ? (
        showAccountsLoader ? (
          <WidgetSkeleton />
        ) : themedAccounts ? (
          <Box sx={chartWrapSx(290)}>
            <HighchartsReact
              highcharts={Highcharts}
              options={themedAccounts}
              containerProps={HC_CONTAINER_PROPS}
            />
          </Box>
        ) : null
      ) : themedOverDue ? (
        <Box sx={chartWrapSx(290)}>
          <HighchartsReact
            highcharts={Highcharts}
            options={themedOverDue}
            containerProps={HC_CONTAINER_PROPS}
          />
        </Box>
      ) : null}
      {!showAccountsData ? (
        <ExdHint>Click an aging bucket to see clients</ExdHint>
      ) : null}
    </WidgetShell>
  );
};

export default ReceivableAgingWidget;
